import union from 'lodash/union';

/**
 * A class to handler mutation types used in store modules. Register all
 * mutations for all modules to an instance of this class and it will generated
 * both namespaced (only for modules that are name spaced) and non-namespaced
 * versions of mutations
 *
 * Ex:
 * const mutationTypes = new MutationTypes();
 *
 * // Adding a module, say 'module1'
 * mutationTypes.addModule('module1');
 *
 * // Adding a namespaced module, say 'module2'
 * mutationTypes.addModule('module2', true);
 *
 * // Registering single mutation for a module, say add mutation 'MUTATION_1'
 * // of module 'module1'
 * mutationTypes.add('module1', 'MUTATION_1');
 *
 * // Registering multiple mutations for a module, say adding mutations
 * // 'MUTATION_2', 'MUTATION_3' of 'module2'
 * mutationTypes.add('module2', [ 'MUTATION_2', 'MUTATION_3 ]);
 *
 * // And then access the generated mutations using the getter 'mutations',
 * // which will return this
 * {
 *   // Belongs to 'module1' which is not namespaced.
 *   MUTATION_1: 'MUTATION_1',
 *
 *   // Belongs to 'module2' which is namespaced.
 *   MUTATION_2: 'MUTATION_2',
 *   MUTATION_2_NAMESPACED: 'module2/MUTATION_2',
 *
 *   // Belongs to 'module2' which is namespaced.
 *   MUTATION_3: 'MUTATION_3',
 *   MUTATION_3_NAMESPACED: 'module2/MUTATION_3'
 * }
 *
 */
class MutationTypes {
  _modules = {};
  _mutations = {};

  // Adds a module
  addModule(module, isNamespaced = false) {
    this._modules[module] = {
      namespaced: isNamespaced,
      mutations: []
    };
  }

  // Adds mutation type/types to a module
  add(module, mutation) {
    const moduleData = this.modules[module];
    if (Array.isArray(mutation)) {
      // Using union so that there will be no duplicate mutations
      moduleData.mutations = union(moduleData.mutations, mutation);
    } else if (!moduleData.mutations.includes(mutation)) {
      moduleData.mutations.push(mutation);
    }

    this._regenerateMutations();
  }

  _regenerateMutations() {
    Object.keys(this.modules).forEach(module => {
      const moduleData = this.modules[module];
      moduleData.mutations.forEach(mutation => {
        this.mutations[mutation] = mutation;
        if (moduleData.namespaced) {
          this.mutations[`${mutation}_NAMESPACED`] = `${module}/${mutation}`;
        }
      });
    });
  }

  get modules() {
    return this._modules;
  }

  get mutations() {
    return this._mutations;
  }
}

export const mutationTypes = new MutationTypes();

export default mutationTypes.mutations;
